/**
 * Ferramentas de teste (so fora de producao, ADMIN pra cima): simular uma compra pelo mesmo caminho do pagamento de
 * verdade (sai o CPF e o link de primeiro acesso), o relogio de teste, que adianta a hora das regras (24 h, 8 h, 2 h,
 * prazo do teste) pra testar sem esperar, e os clientes de exemplo (spec 055): um lote com um cliente em cada situacao.
 */
import {
  Aviso, Botao, BotaoLink, CabecalhoDePagina, Campo, CampoCpf, CampoSelecao, Carregando, Fatos, IconeRelogio, LinkInterno, Motivo, Pilha, Secao, Segmentado, Tabela, TextoCopiavel,
  cpfValido, emailValido, formatarDataHora, soDigitos, type Coluna,
} from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { PLATAFORMA, erroDe, useAcao, useApi, type ClienteDeExemplo, type CompraSimulada, type LoteDeExemplo, type Plataforma, type Relogio } from "../api";
import { usePlanos } from "./Matriculas";

const emailNovo = () => `teste.${Date.now().toString(36)}@exemplo.com`;

export function PaginaFerramentas() {
  return (
    <Pilha espaco={6}>
      <CabecalhoDePagina titulo="Ferramentas de teste" descricao="Pra testar os processos clicando: crie clientes de exemplo, simule uma compra e adiante o relógio das regras de tempo." />
      <ClientesDeExemplo />
      <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 400px), 1fr))" }}>
        <SimularCompra />
        <RelogioDeTeste />
      </div>
    </Pilha>
  );
}

/** Spec 055: um lote com um cliente em cada situacao que o painel mostra, pelas mesmas rotas das telas. */
function ClientesDeExemplo() {
  const { postar } = useApi();
  const qc = useQueryClient();
  const acao = useAcao();
  const [lote, setLote] = useState<LoteDeExemplo | null>(null);
  const criar = async () => {
    let r: LoteDeExemplo | undefined;
    const ok = await acao.rodar(async () => { r = await postar<LoteDeExemplo>("/v1/admin/homologacao/exemplos", {}); });
    if (!ok || !r) return;
    setLote(r);
    await qc.invalidateQueries();
  };
  const criados = lote?.itens.filter((i) => i.ok).length ?? 0;
  const colunas: Coluna<ClienteDeExemplo>[] = [
    { chave: "situacao", rotulo: "Situação", celula: (i) => i.titulo },
    { chave: "cliente", rotulo: "Cliente", celula: (i) => (i.ok && i.pessoa_id ? <><LinkInterno href={`/traders/${i.pessoa_id}`}>{i.nome}</LinkInterno><span className="mtc-tabela__apoio mtc-mono">{i.cpf}</span></> : <Motivo titulo="Não foi criado" detalhe={i.erro} />) },
    {
      chave: "abrir", rotulo: "Abrir", rotuloOculto: true, alinhar: "direita",
      celula: (i) => (
        <div className="mtc-linha" style={{ gap: "var(--e-2)", justifyContent: "flex-end" }}>
          {i.link_primeiro_acesso && <a href={i.link_primeiro_acesso} target="_blank" rel="noreferrer">Link de primeiro acesso</a>}
          {i.matricula_id && <BotaoLink href={`/matriculas/${i.matricula_id}`} tamanho="pequeno">Ver plano</BotaoLink>}
        </div>
      ),
    },
  ];
  return (
    <Secao rotulo="Dados" titulo="Clientes de exemplo">
      <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>Cria um cliente em cada situação que o painel mostra: esperando liberar, em cada etapa, pedidos de avanço e de repasse em cada passo, mensalidade vencida, comprovante em análise, repasses antigos e as vendas deles. Passa pelo mesmo caminho das telas e, depois, é dado normal: mude o que quiser. Cada clique cria um lote novo, sem mexer nos anteriores.</p>
      {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
      <div><Botao carregando={acao.carregando} onClick={() => void criar()}>Criar clientes de exemplo</Botao></div>
      {lote && (
        <div className="mtc-pilha" style={{ gap: "var(--e-4)" }} aria-live="polite">
          <Aviso tipo={criados === lote.itens.length && lote.vendas.ok ? "sucesso" : "alerta"}>
            Lote {lote.lote}: {criados} de {lote.itens.length} clientes criados. {lote.vendas.ok ? `${lote.vendas.enviadas} vendas em Comercial, Vendas.` : `As vendas não foram: ${lote.vendas.erro ?? "erro no serviço de pagamentos"}.`}
          </Aviso>
          <Fatos itens={[{ rotulo: "Senha dos clientes na área", valor: <span data-senha-do-lote={lote.senha}><TextoCopiavel valor={lote.senha} rotulo="Copiar senha" /></span>, detalhe: "Entre com o CPF de qualquer um deles. O primeiro fica sem senha de propósito." }]} />
          <Tabela colunas={colunas} linhas={lote.itens} chave={(i) => String(i.situacao)} cartao="media" />
        </div>
      )}
    </Secao>
  );
}

function SimularCompra() {
  const { postar } = useApi();
  const qc = useQueryClient();
  const planos = usePlanos();
  const acao = useAcao();
  const [plano, setPlano] = useState("");
  const [plataforma, setPlataforma] = useState<Plataforma>("PROFIT_ONE");
  const [nome, setNome] = useState("Trader de Teste");
  const [email, setEmail] = useState(emailNovo);
  const [cpf, setCpf] = useState("");
  const [resultado, setResultado] = useState<(CompraSimulada & { plano: string; email: string; plataforma: Plataforma }) | null>(null);
  const cpfOk = !cpf || cpfValido(cpf);
  const enviar = async (ev: FormEvent) => {
    ev.preventDefault();
    let r: CompraSimulada | undefined;
    const ok = await acao.rodar(async () => { r = await postar<CompraSimulada>("/v1/admin/homologacao/compras", { plano_slug: plano, plataforma, nome: nome.trim(), email: email.trim(), cpf: cpf ? soDigitos(cpf) : undefined }); });
    if (!ok || !r) return;
    setResultado({ ...r, plano: planos.data?.itens.find((p) => p.slug === plano)?.nome ?? plano, email: email.trim(), plataforma });
    setEmail(emailNovo());
    setCpf("");
    await qc.invalidateQueries();
  };
  return (
    <Secao rotulo="Compra" titulo="Simular compra">
      <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>Passa pelo mesmo caminho de uma compra confirmada: cria a pessoa, a matrícula e o e-mail da compra. O e-mail sai simulado; o link de primeiro acesso aparece aqui.</p>
      <form className="mtc-formulario" onSubmit={enviar} noValidate>
        {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
        <CampoSelecao rotulo="Plano" valor={plano} aoMudar={setPlano} vazio="Escolha o plano" required opcoes={(planos.data?.itens ?? []).filter((p) => p.ativo).map((p) => ({ valor: p.slug, rotulo: p.nome }))} />
        <Segmentado rotulo="Plataforma" nome="plataforma-compra" valor={plataforma} aoMudar={setPlataforma} opcoes={[{ valor: "PROFIT_ONE", rotulo: PLATAFORMA.PROFIT_ONE }, { valor: "PROFIT_PRO", rotulo: PLATAFORMA.PROFIT_PRO }]} />
        <Campo rotulo="Nome" valor={nome} aoMudar={setNome} autoComplete="off" />
        <Campo rotulo="E-mail" valor={email} aoMudar={setEmail} type="email" autoComplete="off" mono erro={email && !emailValido(email) ? "Confira o e-mail." : undefined} />
        <CampoCpf rotulo="CPF (opcional)" valor={cpf} aoMudar={setCpf} erro={cpfOk ? undefined : "CPF inválido."} dica="Em branco, sai um CPF válido novo. Use um CPF que já comprou pra testar recompra." />
        <div><Botao type="submit" carregando={acao.carregando} disabled={!plano || nome.trim().length < 2 || !emailValido(email) || !cpfOk}>Simular compra</Botao></div>
      </form>
      {resultado && (
        <div className="mtc-pilha" style={{ gap: "var(--e-4)" }} aria-live="polite">
          <Aviso tipo="sucesso">Compra criada: {resultado.plano}, {PLATAFORMA[resultado.plataforma]}.</Aviso>
          <Fatos itens={[{ rotulo: "CPF", valor: <span data-cpf={resultado.cpf}>{resultado.cpf}</span>, mono: true }, { rotulo: "E-mail", valor: resultado.email, mono: true }]} />
          {resultado.link_primeiro_acesso ? (
            <div className="mtc-pilha" style={{ gap: "var(--e-2)" }}>
              <span className="mtc-rotulo">Link de primeiro acesso</span>
              <TextoCopiavel valor={resultado.link_primeiro_acesso} rotulo="Copiar link" />
              <a href={resultado.link_primeiro_acesso} target="_blank" rel="noreferrer">Abrir o link numa aba nova</a>
            </div>
          ) : (
            <Aviso tipo="info">Esta pessoa já tem senha: entra na área com o CPF e a senha dela.</Aviso>
          )}
          <div className="mtc-linha" style={{ gap: "var(--e-2)" }}>
            {resultado.matricula_ids.map((id, i) => <BotaoLink key={id} href={`/matriculas/${id}`} tamanho="pequeno">Ver matrícula{resultado.matricula_ids.length > 1 ? ` ${i + 1}` : ""}</BotaoLink>)}
          </div>
        </div>
      )}
    </Secao>
  );
}

function RelogioDeTeste() {
  const { ler, postar } = useApi();
  const qc = useQueryClient();
  const acao = useAcao();
  const [horas, setHoras] = useState("");
  const q = useQuery({ queryKey: ["relogio"], queryFn: () => ler<Relogio>("/v1/admin/homologacao/relogio"), retry: false });
  const mexer = (caminho: string, corpo?: unknown) => acao.rodar(async () => {
    const r = await postar<Relogio>(caminho, corpo);
    qc.setQueryData(["relogio"], r);
    // Tudo que depende de hora (prazos, "faltam X h") muda: recarrega o resto.
    await qc.invalidateQueries({ predicate: (x) => x.queryKey[0] !== "relogio" });
  });
  const d = q.data?.deslocamento_horas ?? 0;
  return (
    <Secao rotulo="Tempo" titulo="Relógio de teste" acoes={<IconeRelogio tamanho={22} />}>
      <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>Adianta a hora que as regras leem: 24 h depois do início da etapa pra pedir análise, 8 h entre pedidos, 2 h entre trocas de data, prazo do teste. Vale para o ambiente inteiro.</p>
      {q.isLoading ? <Carregando compacto /> : q.isError ? (
        <Aviso tipo="erro">{erroDe(q.error)}</Aviso>
      ) : (
        <Fatos itens={[{ rotulo: "Hora das regras", valor: q.data ? formatarDataHora(q.data.agora) : "", mono: true }, { rotulo: "Deslocamento", valor: d ? `${d > 0 ? "+" : ""}${Math.round(d * 10) / 10} h` : "Hora real", detalhe: d ? "Adiantado em relação à hora de verdade" : undefined }]} />
      )}
      {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
      <div className="mtc-linha" style={{ gap: "var(--e-2)" }}>
        {[2, 8, 25].map((h) => <Botao key={h} variante="secundario" tamanho="pequeno" disabled={acao.carregando} onClick={() => void mexer("/v1/admin/homologacao/relogio/adiantar", { horas: h })}>+{h} h</Botao>)}
        <Botao variante="secundario" tamanho="pequeno" disabled={acao.carregando} onClick={() => void mexer("/v1/admin/homologacao/relogio/adiantar", { horas: 24 * 46 })}>+46 dias</Botao>
      </div>
      <form className="mtc-linha" style={{ gap: "var(--e-2)", alignItems: "flex-end" }} onSubmit={(ev) => { ev.preventDefault(); if (Number(horas)) void mexer("/v1/admin/homologacao/relogio/adiantar", { horas: Number(horas) }); }}>
        <div style={{ flex: "1 1 160px" }}><Campo rotulo="Adiantar (horas)" valor={horas} aoMudar={(v) => setHoras(v.replace(/[^\d]/g, "").slice(0, 5))} inputMode="numeric" mono placeholder="Ex.: 72" /></div>
        <Botao type="submit" variante="secundario" disabled={!Number(horas) || acao.carregando}>Adiantar</Botao>
      </form>
      <div><Botao variante="fantasma" carregando={acao.carregando} disabled={!d} onClick={() => void mexer("/v1/admin/homologacao/relogio/zerar")}>Voltar para a hora real</Botao></div>
    </Secao>
  );
}
